import { useState, useEffect } from 'react';
import firebase from 'firebase/app';

import { fetchIsStrikeAdmin } from '../../models/fetchIsStrikeAdmin';

import { useTeamDoc } from './useTeamDoc';

const DENIED = { loading: false, allowed: false, isMember: false, isManager: false };

/**
 * What access the signed-in user has to a team.
 *
 * Mirrors the rules in firestore.rules so the UI allows exactly what the
 * database allows. Four ways to qualify:
 *
 *   1. Strike admin — clients/strike/admins/{uid}. Support staff work on teams
 *      they are not members of, which every team rule allows via isAdmin().
 *   2. Team owner — clients/{team.teamOwnerClientId}.ownerId === uid. Read from
 *      the team doc directly, NOT via the members map: the map is written
 *      afterwards by a trigger, so someone who has just created a team is not in
 *      it yet and would otherwise be shut out of their own team.
 *   3. roles: ['admin'] in the team doc's members map.
 *   4. permissions.isTeamAdmin on the member's subcollection doc — what the
 *      "Team Manager" toggle writes. Checking the map alone would miss every
 *      manager promoted through the UI.
 *
 * The team doc comes from the shared subscription, so this and the screen behind
 * it use one listener between them, and it starts loading immediately rather
 * than waiting for the auth session to restore.
 *
 * Fails closed — any error, missing doc or signed-out user resolves to denied.
 *
 * @param {string} teamId
 * @param {{require?: 'member'|'manager'}} options
 * @returns {{loading: boolean, allowed: boolean, isMember: boolean, isManager: boolean}}
 */
export function useTeamAccess(teamId, { require = 'manager' } = {}) {
    const { team, loaded: teamLoaded, error: teamError } = useTeamDoc(teamId);
    // undefined = auth not resolved yet, null = signed out.
    const [uid, setUid] = useState(undefined);
    const [access, setAccess] = useState({ ...DENIED, loading: true });

    useEffect(() => firebase.auth().onAuthStateChanged(
        (user) => setUid(user && user.uid ? user.uid : null),
    ), []);

    useEffect(() => {
        let active = true;

        if (uid === undefined || !teamLoaded) return undefined;
        if (!uid || !teamId || teamError) {
            setAccess(DENIED);
            return undefined;
        }

        const db = firebase.firestore();

        // Never throws — a normal user cannot read the admin list, and that means
        // "not an admin", not "access check failed". This ran inside the
        // Promise.all below, so a rejection here denied the team to every
        // non-admin before membership was ever considered.
        const isStrikeAdmin = () => fetchIsStrikeAdmin(uid);

        const ownsTeamClient = async (teamOwnerClientId) => {
            if (!teamOwnerClientId) return false;
            const snapshot = await db.collection('clients').doc(teamOwnerClientId).get();
            if (snapshot.exists && (snapshot.data() || {}).ownerId === uid) return true;
            // Delegation is owner-equivalence (2026-08-11): a delegated user runs
            // the owning client, teams included. The grant doc is backend-mirrored
            // and readable by its own user; mirrors isTeamDelegate in the rules.
            try {
                const grant = await db.collection('users').doc(uid)
                    .collection('client_roles').doc(teamOwnerClientId).get();
                return grant.exists;
            } catch (err) {
                return false;
            }
        };

        const hasManagerPermission = async (memberClientId) => {
            if (!memberClientId) return false;
            const snapshot = await db.collection('teams').doc(teamId)
                .collection('members').doc(memberClientId).get();
            const permissions = (snapshot.exists && snapshot.data().permissions) || {};
            return permissions.isTeamAdmin === true;
        };

        // POSITIONAL EQUIVALENCE (2026-08-11): a delegate stands in the client's
        // position — owner where the client owns (ownsTeamClient above), MEMBER
        // where the client is a member. Membership is uid-keyed in the members
        // map, so the delegate's own uid is never there; instead, check whether
        // any membership belongs to a client this uid runs (own or delegated).
        const accessibleClientIds = async () => {
            const ids = new Set();
            try {
                const own = await db.collection('clients').where('ownerId', '==', uid).limit(5).get();
                own.docs.forEach((d) => ids.add(d.id));
            } catch (err) { /* fail closed per-source */ }
            try {
                const grants = await db.collection('users').doc(uid).collection('client_roles').get();
                grants.docs.forEach((d) => ids.add(d.id));
            } catch (err) { /* fail closed per-source */ }
            return ids;
        };

        const memberClientIdForPosition = async (teamData) => {
            const membership = (teamData.members || {})[uid] || {};
            if (membership.memberClientId) return membership.memberClientId;
            const mine = await accessibleClientIds();
            const entries = Object.values(teamData.members || {});
            const match = entries.find((m) => m && m.memberClientId && mine.has(m.memberClientId));
            return match ? match.memberClientId : null;
        };

        const resolve = async () => {
            const teamData = team || {};
            const membership = (teamData.members || {})[uid] || {};
            const hasAdminRole = Array.isArray(membership.roles) && membership.roles.includes('admin');

            const [strikeAdmin, isOwner, memberClientId] = await Promise.all([
                isStrikeAdmin(),
                ownsTeamClient(teamData.teamOwnerClientId),
                memberClientIdForPosition(teamData),
            ]);

            let isManager = strikeAdmin || isOwner || hasAdminRole;
            if (!isManager) isManager = await hasManagerPermission(memberClientId);

            return { isMember: strikeAdmin || isOwner || !!memberClientId, isManager };
        };

        resolve()
            .then((result) => {
                if (!active) return;
                setAccess({
                    loading: false,
                    allowed: require === 'member' ? result.isMember : result.isManager,
                    ...result,
                });
            })
            .catch((err) => {
                console.error('Team access check failed:', err);
                if (active) setAccess(DENIED);
            });

        return () => { active = false; };
    }, [teamId, require, uid, team, teamLoaded, teamError]);

    return access;
}

/** Manager-or-above access. Kept for existing call sites. */
export function useTeamAdminAccess(teamId) {
    return useTeamAccess(teamId, { require: 'manager' });
}
